import { useAutocomplete } from '@/lib/useSearch';
import { IPlatform, THit } from '@/types';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import defaultIcon from '../../../public/assets/image-placeholder.png';
import { appSynonymGlobal } from '../../globals/appSynonym.global';
import { SystemIcon } from '../systemIcon/systemIcon.comp';
import { StyledDiv } from './searchBox.styles';

const AutoCompleteHit = ({ hit }: { hit: Partial<THit> }) => {
	return (
		<div className="autocomplete-item flex-wrapper" key={`ac_${hit._id}`}>
			<Image
				width={30}
				height={30}
				src={hit.icon || defaultIcon}
				alt={hit.name || ''}
			/>
			<div className="meta">
				{(hit?.name || '').replace(/\&#x2011;/g, '- ')}
			</div>
			<Link
				href={`/${hit.platform}/${
					(appSynonymGlobal as any)[hit.platform || ''] || 'app'
				}/${hit.slug}`}
			></Link>
		</div>
	);
};

const AutoCompleteCategoryHit = ({ hit }: { hit: any }) => {
	return (
		<div className="autocomplete-item flex-wrapper" key={`ac_${hit._id}`}>
			<div className="meta">
				{hit.displayName} for {hit.platform}
			</div>
			<Link href={`/${hit.platform}/category/${hit.slug}`}></Link>
		</div>
	);
};

export const LocalSearchBox = ({
	platform = null,
	searchAsYouType = false,
	enableAutoComplete = false,
	platforms = null,
	autoFocus = true,
	initialQuery = null,
	placeholder = 'Search...',
	showCategories = true,
}: any) => {
	const { q } = queryString.parse(
		typeof window !== 'undefined' ? window?.location.search : ''
	);
	const [autoCompleteOpened, setAutoCompleteOpened] = useState<boolean>(false);
	const [localQuery, setLocalQuery] = useState<string>(
		(initialQuery as string) || (q as string) || ''
	);
	const navigate = useRouter();
	const { apps, totalApps, categories, totalCategories } = useAutocomplete(
		enableAutoComplete && autoCompleteOpened ? localQuery : '',
		(platform as any)?.name
	);

	function resultsUrl(query: string) {
		return `${
			(platform as any)?.name ? '/' + (platform as any)?.name : ''
		}/results?q=${encodeURIComponent(query)}&orderBy=popular`;
	}

	function submit(e: any) {
		e.preventDefault();
		setAutoCompleteOpened(false);
		navigate.push(resultsUrl(localQuery));
	}

	function handleOutsideClick(e: MouseEvent) {
		e.stopPropagation();

		const target = e.target as HTMLElement;
		if (target && !target.closest('.search-box-wrapper')) {
			setAutoCompleteOpened(false);
		}
	}

	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}

		if (typeof window !== 'undefined') {
			window.addEventListener('mousedown', handleOutsideClick);

			return () => {
				window.removeEventListener('mousedown', handleOutsideClick);
			};
		}
	}, []);

	return (
		<StyledDiv>
			<div className="search-box">
				<div className="search-box-wrapper">
					<form
						noValidate
						action=""
						role="search"
						className="ais-SearchBox-form"
						onSubmit={submit}
					>
						<button type="submit" title="Submit" className="ais-SearchBox-submit">
							<SystemIcon type="search" />
						</button>
						<input
							type="search"
							className="ais-SearchBox-input"
							placeholder={placeholder}
							autoFocus={autoFocus}
							autoComplete="off"
							autoCorrect="off"
							autoCapitalize="off"
							spellCheck={false}
							value={localQuery}
							onChange={(e) => {
								const value = e.target.value;
								setLocalQuery(value);
								if (enableAutoComplete) {
									setAutoCompleteOpened(!!value);
								} else if (searchAsYouType) {
									navigate.push(resultsUrl(value), undefined, {
										shallow: true,
									});
								}
							}}
						/>
						{localQuery && (
							<button
								type="reset"
								title="Clear"
								className="ais-SearchBox-reset"
								onClick={() => {
									setLocalQuery('');
									setAutoCompleteOpened(false);
									if (searchAsYouType && !enableAutoComplete) {
										navigate.push(resultsUrl(''), undefined, {
											shallow: true,
										});
									}
								}}
							>
								<SystemIcon type="close" />
							</button>
						)}
					</form>
					{localQuery && autoCompleteOpened && (
						<div className="autocomplete-box">
							{showCategories && categories.length > 0 && (
								<>
									<h6>
										Categories{' '}
										<span>{totalCategories.toLocaleString()} results</span>
									</h6>
									<div className="ais-Hits-list">
										{categories.map((c) => (
											<AutoCompleteCategoryHit key={`ac_${c._id}`} hit={c} />
										))}
									</div>
								</>
							)}
							{!platform && platforms && (
								<>
									<h6>Platforms</h6>
									<div className="ais-Hits-list">
										{(platforms as IPlatform[]).map((p) => (
											<div
												className="autocomplete-item flex-wrapper"
												key={`ac_${p.name}`}
											>
												<div className="meta">
													{localQuery} in {p.displayName}
												</div>
												<Link
													href={`/${p.name}/results?q=${localQuery}&orderBy=popular`}
												></Link>
											</div>
										))}
									</div>
								</>
							)}
							{apps.length > 0 && (
								<>
									<h6>
										Apps <span>{totalApps.toLocaleString()} results</span>
									</h6>
									<div className="ais-Hits-list">
										{apps.map((hit) => (
											<AutoCompleteHit key={`ac_${hit._id}`} hit={hit} />
										))}
									</div>
								</>
							)}
						</div>
					)}
				</div>
			</div>
		</StyledDiv>
	);
};
